
import { Component, Emit, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import { Sinister } from '../../../shared/types/sinisters/sinisterInfo';
import { Note, SamplingClassification } from '../../../shared/types/notes/notes';
import InspectionAppraisers from './appraisers/InspectionAppraisers.vue';
import InspectionNotes from './notes/InspectionNotes.vue';
import InspectionSamplingReport from '../samplings/components/InspectionSamplingReport.vue';
import { defaultUserPrivilege, PrivilegeValue, PRIVILEGE_KEYS } from '../../../shared/privileges/privileges';
import { buildClassificationColorMap } from '../samplings/colors';
import {
  buildClassificationNameMap,
  enrichNotesWithClassificationNames,
  getUnclassifiedSamplingsFromNotes,
  UnclassifiedSampling,
} from '../samplings/enrich-notes';
import { ClaimsPort, ItemClaimsPort } from '../../../core/api/ports';
import { InjectionKey } from '../../../core/injection-keys';
import { InspectionTab, TenantConfig } from '../../../tenants/types';

const TAB_LABELS: Record<InspectionTab, string> = {
  [InspectionTab.Appraisers]: 'Tasadores',
  [InspectionTab.Notes]: 'Notas',
  [InspectionTab.SamplingReport]: 'Acta de tasación',
};

const TAB_ICONS: Record<InspectionTab, string> = {
  [InspectionTab.Appraisers]: 'mdi-account',
  [InspectionTab.Notes]: 'mdi-note-text',
  [InspectionTab.SamplingReport]: 'mdi-file-document',
};

/**
 * Sección "Inspección" en pestañas. Las pestañas y su orden salen de `pages.claimDetail.inspectionTabs`.
 * El muestreo (clasificaciones, marcadores y "Acta de tasación") requiere `features.samplingReport` y el
 * privilegio `CLAIM` del item (`shared/privileges/privileges.ts`).
 */
@Component({
  components: {
    InspectionAppraisers,
    InspectionNotes,
    InspectionSamplingReport,
  },
})
export default class InspectionSection extends Vue {
  @Inject(InjectionKey.Tenant) readonly tenant!: TenantConfig;
  @Inject(InjectionKey.ClaimsApi) readonly claimsApi!: ClaimsPort;
  @Inject({ from: InjectionKey.ItemClaimsApi, default: null }) readonly itemClaimsApi!: ItemClaimsPort | null;
  @Prop({ required: true }) sinister!: Sinister;
  @Prop({ default: () => defaultUserPrivilege }) privilege!: PrivilegeValue;
  @Prop({ default: null }) selectedNoteId!: number | null;

  readonly InspectionTab = InspectionTab;
  readonly tabLabels = TAB_LABELS;
  readonly tabIcons = TAB_ICONS;

  activeTab: number = 0;

  // Selección desde el mapa: llevar al usuario a la pestaña de notas (si está habilitada)
  @Watch('selectedNoteId')
  onSelectedNoteIdChange(noteId: number | null) {
    const notesTabIndex = this.visibleTabs.indexOf(InspectionTab.Notes);
    if (noteId != null && notesTabIndex > -1) this.activeTab = notesTabIndex;
  }
  samplingClassifications: SamplingClassification[] = [];
  classificationNames: Record<number, string> = {};
  classificationColors: Record<number, string> = {};
  unclassifiedSamplings: UnclassifiedSampling[] = [];
  loadingClassifications: boolean = false;

  /** Muestreo habilitado para este item (antes `showSamplingReportTab`: privilegio `CLAIM`). */
  get samplingEnabled(): boolean {
    return this.tenant.features.samplingReport
      && !!this.itemClaimsApi
      && this.privilege === PRIVILEGE_KEYS.CLAIM;
  }

  get visibleTabs(): InspectionTab[] {
    const tabs = this.tenant.pages.claimDetail ? this.tenant.pages.claimDetail.inspectionTabs : [];
    return tabs.filter(tab => tab !== InspectionTab.SamplingReport || this.samplingEnabled);
  }

  mounted() {
    this.loadNotesForMap();
  }

  /** Load notes (and classifications if CLAIM) so markers appear on the map */
  loadNotesForMap() {
    const notesPromise = this.claimsApi.getSinisterNotes(this.sinister.id);
    const classificationsPromise = this.samplingEnabled && this.itemClaimsApi
      ? (() => {
          this.loadingClassifications = true;
          return this.itemClaimsApi.getSamplingClassifications(this.sinister.id)
            .then(res => {
              const list = res.sampling_classifications || [];
              this.samplingClassifications = list;
              this.classificationNames = buildClassificationNameMap(list);
              this.classificationColors = buildClassificationColorMap(list);
            })
            .catch(() => {
              this.samplingClassifications = [];
              this.classificationNames = {};
              this.classificationColors = {};
            })
            .finally(() => {
              this.loadingClassifications = false;
            });
        })()
      : Promise.resolve();

    Promise.all([notesPromise, classificationsPromise])
      .then(([notesRes]) => {
        const notes = notesRes.ranch_notes || [];
        const enriched = this.samplingEnabled
          ? enrichNotesWithClassificationNames(
              notes,
              this.classificationNames,
              this.classificationColors
            )
          : notes;
        this.onSeeNotesInMap(enriched);
      })
      .catch(() => {
        // Map notes are best-effort on load; NoteForm will surface errors when the tab opens
      });
  }

  @Emit('see-notes-in-map')
  onSeeNotesInMap(notes: Note[]) {
    if (this.samplingEnabled) {
      this.unclassifiedSamplings = getUnclassifiedSamplingsFromNotes(notes);
    } else {
      this.unclassifiedSamplings = [];
    }
    return notes;
  }
}
